import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Editor } from '@tiptap/core';
import { OsmosLinkDialogComponent, OsmosLinkDialogData } from './osmos-link-dialog.component';

type BlockValue = 'paragraph' | 'h1' | 'h2' | 'h3';

/**
 * Presentational toolbar for <osmos-editor>. Holds no state of its own —
 * every button reads its active state from the editor and acts on it.
 *
 * Change detection is deliberately Default, not OnPush: the editor lives
 * outside the Angular zone, so OnPush would leave the active states stale.
 */
@Component({
  selector: 'osmos-editor-toolbar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSelectModule,
    MatDialogModule,
  ],
  changeDetection: ChangeDetectionStrategy.Default,
  template: `
    <div class="osmos-toolbar" role="toolbar" aria-label="Text formatting">
      <mat-select
        class="osmos-toolbar__block"
        [ngModel]="currentBlock()"
        (ngModelChange)="setBlock($event)"
        aria-label="Paragraph style"
      >
        <mat-option value="paragraph">Paragraph</mat-option>
        <mat-option value="h1">Heading 1</mat-option>
        <mat-option value="h2">Heading 2</mat-option>
        <mat-option value="h3">Heading 3</mat-option>
      </mat-select>

      <span class="osmos-toolbar__sep"></span>

      <button
        type="button"
        mat-icon-button
        matTooltip="Bold"
        [attr.aria-pressed]="editor.isActive('bold')"
        [class.is-active]="editor.isActive('bold')"
        (click)="editor.chain().focus().toggleBold().run()"
      >
        <mat-icon>format_bold</mat-icon>
      </button>
      <button
        type="button"
        mat-icon-button
        matTooltip="Italic"
        [attr.aria-pressed]="editor.isActive('italic')"
        [class.is-active]="editor.isActive('italic')"
        (click)="editor.chain().focus().toggleItalic().run()"
      >
        <mat-icon>format_italic</mat-icon>
      </button>
      <button
        type="button"
        mat-icon-button
        matTooltip="Underline"
        [attr.aria-pressed]="editor.isActive('underline')"
        [class.is-active]="editor.isActive('underline')"
        (click)="editor.chain().focus().toggleUnderline().run()"
      >
        <mat-icon>format_underlined</mat-icon>
      </button>

      <span class="osmos-toolbar__sep"></span>

      <button
        type="button"
        mat-icon-button
        matTooltip="Bulleted list"
        [attr.aria-pressed]="editor.isActive('bulletList')"
        [class.is-active]="editor.isActive('bulletList')"
        (click)="editor.chain().focus().toggleBulletList().run()"
      >
        <mat-icon>format_list_bulleted</mat-icon>
      </button>
      <button
        type="button"
        mat-icon-button
        matTooltip="Numbered list"
        [attr.aria-pressed]="editor.isActive('orderedList')"
        [class.is-active]="editor.isActive('orderedList')"
        (click)="editor.chain().focus().toggleOrderedList().run()"
      >
        <mat-icon>format_list_numbered</mat-icon>
      </button>

      <span class="osmos-toolbar__sep"></span>

      <button
        type="button"
        mat-icon-button
        matTooltip="Align left"
        [class.is-active]="editor.isActive({ textAlign: 'left' })"
        (click)="editor.chain().focus().setTextAlign('left').run()"
      >
        <mat-icon>format_align_left</mat-icon>
      </button>
      <button
        type="button"
        mat-icon-button
        matTooltip="Align centre"
        [class.is-active]="editor.isActive({ textAlign: 'center' })"
        (click)="editor.chain().focus().setTextAlign('center').run()"
      >
        <mat-icon>format_align_center</mat-icon>
      </button>
      <button
        type="button"
        mat-icon-button
        matTooltip="Align right"
        [class.is-active]="editor.isActive({ textAlign: 'right' })"
        (click)="editor.chain().focus().setTextAlign('right').run()"
      >
        <mat-icon>format_align_right</mat-icon>
      </button>

      <span class="osmos-toolbar__sep"></span>

      <button
        type="button"
        mat-icon-button
        matTooltip="Link"
        [class.is-active]="editor.isActive('link')"
        (click)="toggleLink()"
      >
        <mat-icon>link</mat-icon>
      </button>
      <button type="button" mat-icon-button matTooltip="Clear formatting" (click)="clearFormatting()">
        <mat-icon>format_clear</mat-icon>
      </button>

      <span class="osmos-toolbar__sep"></span>

      <button type="button" mat-icon-button matTooltip="Undo" (click)="editor.chain().focus().undo().run()">
        <mat-icon>undo</mat-icon>
      </button>
      <button type="button" mat-icon-button matTooltip="Redo" (click)="editor.chain().focus().redo().run()">
        <mat-icon>redo</mat-icon>
      </button>
    </div>
  `,
  styles: [
    `
      .osmos-toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 2px;
        padding: 4px 6px;
        border-bottom: 1px solid #e3ecf1;
      }

      .osmos-toolbar__block {
        width: 140px;
        font-size: 13px;
      }

      .osmos-toolbar__sep {
        width: 1px;
        align-self: stretch;
        margin: 4px 6px;
        background: #e3ecf1;
      }

      .is-active {
        background: rgba(25, 118, 210, 0.12);
      }
    `,
  ],
})
export class OsmosEditorToolbarComponent {
  @Input({ required: true }) editor!: Editor;

  constructor(private readonly dialog: MatDialog) {}

  currentBlock(): BlockValue {
    if (this.editor.isActive('heading', { level: 1 })) {
      return 'h1';
    }
    if (this.editor.isActive('heading', { level: 2 })) {
      return 'h2';
    }
    if (this.editor.isActive('heading', { level: 3 })) {
      return 'h3';
    }
    return 'paragraph';
  }

  setBlock(value: BlockValue): void {
    if (value === 'paragraph') {
      this.editor.chain().focus().setParagraph().run();
      return;
    }
    const level = Number(value.slice(1)) as 1 | 2 | 3;
    this.editor.chain().focus().setHeading({ level }).run();
  }

  /**
   * Opens a Material dialog rather than window.prompt: a native prompt is not
   * reachable by keyboard or screen reader, is suppressed outright in some
   * browser configurations, and gives no way to explain a rejected address.
   *
   * The dialog closes with a URL to apply, null to remove the link, or
   * undefined when cancelled.
   */
  toggleLink(): void {
    const data: OsmosLinkDialogData = { href: this.editor.getAttributes('link')['href'] ?? '' };

    this.dialog
      .open(OsmosLinkDialogComponent, { data, autoFocus: true, restoreFocus: true })
      .afterClosed()
      .subscribe((result: string | null | undefined) => {
        // The dialog outlives the editor if the item modal is closed while it
        // is open; acting on a destroyed editor throws.
        if (!this.editor || this.editor.isDestroyed) {
          return;
        }
        if (result === undefined) {
          this.editor.chain().focus().run();
          return;
        }
        if (result === null) {
          this.editor.chain().focus().unsetLink().run();
          return;
        }
        this.editor.chain().focus().extendMarkRange('link').setLink({ href: result }).run();
      });
  }

  /**
   * Removes marks AND block types. The single most useful button for cleaning
   * up descriptions pasted from Word before this editor existed.
   */
  clearFormatting(): void {
    this.editor.chain().focus().unsetAllMarks().clearNodes().run();
  }
}
